import { acquireFirebaseAppCheckToken } from './propertyTeaserClient';

/**
 * Centralized API Client for ADU Lab
 * 
 * This client uses Firebase Hosting rewrites to eliminate CORS issues.
 * All API calls go through /api/* paths which are proxied to Cloud Functions.
 * 
 * Benefits:
 * - No CORS preflight requests (faster)
 * - Environment-agnostic (works same in dev/staging/prod)
 * - Centralized error handling
 * - Easy logging and debugging
 */

class APIClient {
  constructor() {
    // Use relative paths - Firebase Hosting handles routing
    this.baseURL = '/api';
    
    // Fallback to direct URL if rewrite fails
    // This ensures backward compatibility during migration
    this.fallbackURL = import.meta.env.VITE_API_URL || 'https://us-central1-adu-lab-uat.cloudfunctions.net';
    
    // Track if we should use fallback (for debugging)
    this.useFallback = false;
  }

  /**
   * Make a request with automatic fallback
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}/${endpoint}`;
    
    try {
      // First try the rewrite path
      let response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      });

      // If rewrite returns 404, it might not be deployed yet
      if (response.status === 404 && !this.useFallback) {
        console.log(`Rewrite not available for ${endpoint}, trying direct URL`);
        
        // Try direct URL as fallback
        const fallbackUrl = `${this.fallbackURL}/${endpoint}`;
        response = await fetch(fallbackUrl, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            ...options.headers
          }
        });
        
        // Remember to use fallback for future requests in this session
        this.useFallback = true;
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || error.message || `Request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      
      // If it's a network error and we haven't tried fallback yet
      if (error.name === 'TypeError' && error.message === 'Failed to fetch' && !this.useFallback) {
        console.log(`Network error, trying direct URL for ${endpoint}`);
        
        try {
          const fallbackUrl = `${this.fallbackURL}/${endpoint}`;
          const response = await fetch(fallbackUrl, {
            ...options,
            headers: {
              'Content-Type': 'application/json',
              ...options.headers
            }
          });
          
          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || error.message || `Request failed: ${response.status}`);
          }
          
          this.useFallback = true;
          return await response.json();
        } catch (fallbackError) {
          console.error(`Fallback also failed for ${endpoint}`, fallbackError);
          throw fallbackError;
        }
      }
      
      throw error;
    }
  }

  /**
   * Process an assessment (Tier 1)
   */
  async processAssessment(assessmentData, requestOptions = {}) {
    const suppliedRequestId = typeof requestOptions.requestId === 'string'
      ? requestOptions.requestId.trim()
      : '';
    const requestId = suppliedRequestId || (globalThis.crypto?.randomUUID
      ? globalThis.crypto.randomUUID()
      : `assessment-${Date.now()}-${Math.random().toString(16).slice(2)}`);
    let appCheckToken = '';
    try {
      appCheckToken = await acquireFirebaseAppCheckToken();
    } catch {
      // Delivery remains available without an attestation; the server then
      // withholds vendor fan-out authority rather than rejecting the lead.
    }
    return this.request('simpleAssessment', {
      method: 'POST',
      headers: {
        'X-Request-Id': requestId,
        ...(appCheckToken ? { 'X-Firebase-AppCheck': appCheckToken } : {}),
      },
      body: JSON.stringify(assessmentData),
    });
  }

  /**
   * Create a Stripe checkout session
   */
  async createCheckoutSession(data) {
    return this.request('createCheckoutSessionSimple', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Get checkout session details
   */
  async getCheckoutSession(sessionId) {
    return this.request('getCheckoutSession', {
      method: 'POST',
      body: JSON.stringify({ sessionId })
    });
  }

  /**
   * Get public assessment by token
   */
  async getPublicAssessment(token) {
    return this.request(`getPublicAssessment?token=${token}`);
  }

  /**
   * Claim an assessment for a user
   */
  async claimAssessment(data) {
    return this.request('claimAssessment', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Create Stripe customer portal session
   */
  async createPortalSession(data) {
    return this.request('createPortalSession', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Get user's subscription status
   */
  async getSubscriptionStatus(userId) {
    return this.request('getSubscriptionStatus', {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
  }

  /**
   * Handle Stripe webhook (usually not called from frontend)
   */
  async handleStripeWebhook(data) {
    return this.request('handleStripeWebhook', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Test CORS configuration
   */
  async testCORS() {
    return this.request('testCORS');
  }

  /**
   * Generic GET request
   */
  async get(endpoint) {
    return this.request(endpoint, {
      method: 'GET'
    });
  }

  /**
   * Generic POST request
   */
  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Generic PUT request
   */
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Generic DELETE request
   */
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE'
    });
  }
}

// Export singleton instance
const apiClient = new APIClient();
export default apiClient;

// Also export the class for testing or multiple instances
export { APIClient };
